import React, { useEffect, useRef, useState } from "react";
import {
    getMeetings, getEvents, getSplits, getAgeGroups, getLeaders,
    Meeting, MikaEvent, SplitConfig, AgeGroup, LeaderItem,
} from "./mikaApi";
import {
    ResultPanelConfig, loadPanels, savePanels, newPanelId,
    loadLastSelection, saveLastSelection,
} from "./resultsPanels";

/**
 * Fristående, helskärms-/kioskvänlig visningswebb för live-resultat (/results).
 * Modulär: konfigurera Meeting → Event → Split → Klass och docka en panel som
 * visar topplistan för den splitten. Läser mika:timing App-API direkt (publikt).
 */

const REFRESH_MS = 25000;

// ---------- En dockad panel: hämtar och visar topplistan ----------
const ResultPanel = ({ config, onRemove }: { config: ResultPanelConfig; onRemove: (id: string) => void }) => {
    const [leaders, setLeaders] = useState<LeaderItem[] | null>(null);
    const [updated, setUpdated] = useState<string>("");
    const [error, setError] = useState(false);
    const timer = useRef<any>(null);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            const rows = await getLeaders({
                idMeeting: config.idMeeting, key: config.eventKey, split: config.split,
                sex: config.sex, ageGroup: config.ageGroup, count: config.count,
            });
            if (!alive) { return; }
            setError(rows.length === 0);
            setLeaders(rows.slice(0, config.count));
            const now = new Date();
            setUpdated(now.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
        };
        load();
        timer.current = setInterval(load, REFRESH_MS);
        return () => { alive = false; if (timer.current) { clearInterval(timer.current); } };
    }, [config.idMeeting, config.eventKey, config.split, config.sex, config.ageGroup, config.count]);

    const name = (l: LeaderItem) =>
        [l.firstname, l.lastname].filter(Boolean).join(" ") || l.displayName || "–";

    return (
        <div style={s.panel}>
            <div style={s.panelHead}>
                <div style={{ overflow: "hidden" }}>
                    <div style={s.panelTitle}>{config.eventName}</div>
                    <div style={s.panelSub}>
                        {config.splitName}
                        {config.classLabel ? ` · ${config.classLabel}` : ""}
                        <span style={{ color: "#5a6b7a" }}> · {config.meetingTitle}</span>
                    </div>
                </div>
                <button style={s.panelClose} onClick={() => onRemove(config.id)} title="Ta bort panel">✕</button>
            </div>

            <div style={s.tableWrap}>
                {leaders === null &&
                    <div style={s.panelMsg}>Hämtar…</div>}
                {leaders !== null && leaders.length === 0 &&
                    <div style={s.panelMsg}>{error ? "Inga resultat ännu för denna split." : "Tomt."}</div>}
                {leaders !== null && leaders.length > 0 &&
                    <table style={s.table}>
                        <tbody>
                            {leaders.map((l, i) =>
                                <tr key={l.idParticipant || i} style={i === 0 ? s.rowLeader : (i % 2 ? s.rowAlt : undefined)}>
                                    <td style={i === 0 ? s.tdRankLeader : s.tdRank}>{i + 1}</td>
                                    <td style={s.tdBib}>{l.startNo}</td>
                                    <td style={s.tdName}>
                                        <div style={s.nameMain}>{name(l)}</div>
                                        <div style={s.nameSub}>
                                            {[l.club || l.addressCity, l.ageGroup, l.nationality].filter(Boolean).join(" · ")}
                                        </div>
                                    </td>
                                    <td style={s.tdTime}>{l.splitTime || "–"}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                }
            </div>
            <div style={s.panelFoot}>Uppdaterad {updated || "…"}</div>
        </div>
    );
};

// ---------- Guiden för att lägga till paneler ----------
const AddPanel = ({ onAddMany, onClose }: { onAddMany: (c: ResultPanelConfig[]) => void; onClose: () => void }) => {
    const [meetings, setMeetings] = useState<Meeting[]>([]);
    const [events, setEvents] = useState<MikaEvent[]>([]);
    const [splits, setSplits] = useState<SplitConfig[]>([]);
    const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);

    const remembered = loadLastSelection();
    const [idMeeting, setIdMeeting] = useState(remembered.idMeeting || "");
    const [eventKey, setEventKey] = useState(remembered.eventKey || "");
    const [selectedSplits, setSelectedSplits] = useState<string[]>([]);
    const [classValue, setClassValue] = useState(remembered.classValue || "");   // "", "sex:M", "sex:W", "ag:<idx>"
    const [count, setCount] = useState(remembered.count || 15);
    const [loading, setLoading] = useState(false);

    // Ladda listor. Om senaste val finns, återställ hela kedjan (lopp + event + splits/klasser).
    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            const ms = await getMeetings();
            if (!alive) { return; }
            setMeetings(ms);
            if (remembered.idMeeting && ms.some(m => m.idMeeting === remembered.idMeeting)) {
                const evs = await getEvents(remembered.idMeeting);
                if (!alive) { return; }
                setEvents(evs);
                if (remembered.eventKey && evs.some(e => e.key === remembered.eventKey)) {
                    const [sp, ag] = await Promise.all([
                        getSplits(remembered.idMeeting, remembered.eventKey),
                        getAgeGroups(remembered.idMeeting, remembered.eventKey),
                    ]);
                    if (!alive) { return; }
                    setSplits(sp);
                    setAgeGroups(ag);
                }
            }
            setLoading(false);
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onMeeting = async (id: string) => {
        setIdMeeting(id); setEventKey(""); setSelectedSplits([]); setClassValue("");
        setEvents([]); setSplits([]); setAgeGroups([]);
        if (!id) { return; }
        setLoading(true);
        setEvents(await getEvents(id));
        setLoading(false);
    };

    const onEvent = async (key: string) => {
        setEventKey(key); setSelectedSplits([]); setClassValue("");
        setSplits([]); setAgeGroups([]);
        if (!key) { return; }
        setLoading(true);
        const [sp, ag] = await Promise.all([getSplits(idMeeting, key), getAgeGroups(idMeeting, key)]);
        setSplits(sp);
        setAgeGroups(ag);
        setLoading(false);
    };

    const toggleSplit = (key: string) => {
        setSelectedSplits(prev => prev.indexOf(key) >= 0 ? prev.filter(k => k !== key) : [...prev, key]);
    };
    const allSelected = splits.length > 0 && selectedSplits.length === splits.length;
    const toggleAll = () => setSelectedSplits(allSelected ? [] : splits.map(sp => sp.key));

    const meeting = meetings.find(m => m.idMeeting === idMeeting);
    const event = events.find(e => e.key === eventKey);

    const resolveClass = (): { sex?: string; ageGroup?: string; label?: string } => {
        if (!classValue) { return {}; }
        if (classValue === "sex:M") { return { sex: "M", label: "Herrar" }; }
        if (classValue === "sex:W") { return { sex: "W", label: "Damer" }; }
        if (classValue.startsWith("ag:")) {
            const ag = ageGroups[parseInt(classValue.slice(3), 10)];
            if (ag) { return { sex: ag.sex, ageGroup: ag.key, label: ag.name }; }
        }
        return {};
    };

    const canAdd = !!idMeeting && !!eventKey && selectedSplits.length > 0;

    const add = () => {
        if (!canAdd || !meeting || !event) { return; }
        const cls = resolveClass();
        // En panel per markerad split, i splitlistans ordning
        const configs: ResultPanelConfig[] = splits
            .filter(sp => selectedSplits.indexOf(sp.key) >= 0)
            .map(sp => ({
                id: newPanelId(),
                idMeeting, meetingTitle: meeting.titleText,
                eventKey, eventName: event.nameText || event.key,
                split: sp.key, splitName: sp.km ? `${sp.name} (${sp.km} km)` : sp.name,
                sex: cls.sex, ageGroup: cls.ageGroup, classLabel: cls.label,
                count,
            }));
        saveLastSelection({ idMeeting, eventKey, classValue, count });
        onAddMany(configs);
    };

    return (
        <div style={s.overlay}>
            <div style={s.wizard}>
                <div style={s.wizardHead}>
                    <span>Lägg till paneler</span>
                    <button style={s.linkBtn} onClick={onClose}>Stäng</button>
                </div>

                <label style={s.field}>
                    <span style={s.label}>Meeting</span>
                    <select style={s.select} value={idMeeting} onChange={e => onMeeting(e.target.value)}>
                        <option value="">Välj meeting…</option>
                        {meetings.map(m => <option key={m.idMeeting} value={m.idMeeting}>{m.titleText}</option>)}
                    </select>
                </label>

                <label style={s.field}>
                    <span style={s.label}>Event</span>
                    <select style={s.select} value={eventKey} disabled={!idMeeting} onChange={e => onEvent(e.target.value)}>
                        <option value="">Välj event…</option>
                        {events.map(e => <option key={e.idEvent} value={e.key}>{e.nameText || e.key}</option>)}
                    </select>
                </label>

                <div style={s.field}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={s.label}>Splits {selectedSplits.length > 0 ? `(${selectedSplits.length} valda)` : ""}</span>
                        {splits.length > 0 &&
                            <button style={s.linkAction} onClick={toggleAll}>{allSelected ? "Avmarkera alla" : "Markera alla"}</button>}
                    </div>
                    <div style={s.splitBox}>
                        {!eventKey && <div style={{ color: VL.gray400, padding: 6 }}>Välj event först…</div>}
                        {eventKey && splits.length === 0 && !loading && <div style={{ color: VL.gray400, padding: 6 }}>Inga splits.</div>}
                        {splits.map(sp =>
                            <label key={sp.key} style={s.splitRow}>
                                <input type="checkbox" checked={selectedSplits.indexOf(sp.key) >= 0} onChange={() => toggleSplit(sp.key)} />
                                <span style={{ flex: 1 }}>
                                    {sp.name}{sp.km ? ` – ${sp.km} km` : ""}
                                    {sp.hasData === false ? <span style={{ color: VL.gray400 }}> · ingen data</span> : ""}
                                </span>
                            </label>
                        )}
                    </div>
                </div>

                <label style={s.field}>
                    <span style={s.label}>Klass</span>
                    <select style={s.select} value={classValue} disabled={!eventKey} onChange={e => setClassValue(e.target.value)}>
                        <option value="">Alla klasser</option>
                        <option value="sex:M">Alla herrar</option>
                        <option value="sex:W">Alla damer</option>
                        {ageGroups.map((ag, i) => <option key={ag.idAgeGroup} value={`ag:${i}`}>{ag.name}</option>)}
                    </select>
                </label>

                <label style={s.field}>
                    <span style={s.label}>Antal rader</span>
                    <select style={s.select} value={count} onChange={e => setCount(parseInt(e.target.value, 10))}>
                        {[5, 10, 15, 20, 25, 30].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                </label>

                {loading && <div style={{ color: VL.gray600, padding: "4px 0" }}>Laddar…</div>}

                <button style={{ ...s.primaryBtn, opacity: canAdd ? 1 : 0.4 }} disabled={!canAdd} onClick={add}>
                    {selectedSplits.length > 1 ? `Docka ${selectedSplits.length} paneler` : "Docka panel"}
                </button>
            </div>
        </div>
    );
};

// ---------- Sidan ----------
const ResultsDisplay = () => {
    const [panels, setPanels] = useState<ResultPanelConfig[]>(loadPanels());
    const [addOpen, setAddOpen] = useState(false);
    const [barHidden, setBarHidden] = useState(false);

    const update = (next: ResultPanelConfig[]) => { setPanels(next); savePanels(next); };
    const addPanels = (cs: ResultPanelConfig[]) => { if (cs.length) { update([...panels, ...cs]); } setAddOpen(false); };
    const removePanel = (id: string) => { update(panels.filter(p => p.id !== id)); };

    const toggleFullscreen = () => {
        const doc: any = document;
        const el: any = document.documentElement;
        if (!doc.fullscreenElement) {
            (el.requestFullscreen || el.webkitRequestFullscreen || (() => { })).call(el);
        } else {
            (doc.exitFullscreen || doc.webkitExitFullscreen || (() => { })).call(doc);
        }
    };

    return (
        <div style={s.page}>
            {barHidden
                ? <button style={s.showBar} onClick={() => setBarHidden(false)} title="Visa verktygsfält">⚙</button>
                : <div style={s.bar}>
                    <span style={s.brand}>Vasaloppet</span>
                    <span style={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }}>Live-resultat</span>
                    <div style={{ flex: 1 }} />
                    <button style={s.barBtnPrimary} onClick={() => setAddOpen(true)}>+ Lägg till panel</button>
                    <button style={s.barBtn} onClick={toggleFullscreen}>Helskärm</button>
                    <button style={s.barBtn} onClick={() => setBarHidden(true)}>Dölj verktygsfält</button>
                </div>
            }

            {panels.length === 0
                ? <div style={s.empty}>
                    <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>Inga paneler ännu</div>
                    <div style={{ color: VL.gray600, marginBottom: 20 }}>Lägg till en panel för att visa en topplista för en split.</div>
                    <button style={{ ...s.primaryBtn, width: "auto", padding: "14px 28px" }} onClick={() => setAddOpen(true)}>+ Lägg till panel</button>
                </div>
                : <div style={s.grid}>
                    {panels.map(p => <ResultPanel key={p.id} config={p} onRemove={removePanel} />)}
                </div>
            }

            {addOpen && <AddPanel onAddMany={addPanels} onClose={() => setAddOpen(false)} />}
        </div>
    );
};

// ---------- Vasaloppets grafiska profil (vasaloppet.se) ----------
const VL = {
    ink: "#222223",
    blue: "#0048AD",        // vl-color-blue-extra-dark (primär)
    blueBright: "#4FACFC",  // vl-color-blue
    yellow: "#FFCF54",      // vl-color-yellow
    yellowDark: "#F5C13B",
    yellowTint: "#FFF6DE",
    gray25: "#f8f8f8",
    gray50: "#f0f0f0",
    gray200: "#d4d4d4",
    gray400: "#a9a9a9",
    gray600: "#818181",
};
const FONT = "'ff-meta-web-pro', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const s: { [k: string]: React.CSSProperties } = {
    page: { minHeight: "100vh", background: VL.gray50, color: VL.ink, fontFamily: FONT },
    bar: { display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", background: VL.ink, color: "#fff", position: "sticky", top: 0, zIndex: 10 },
    brand: { fontSize: 18, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase" },
    barBtn: { padding: "9px 16px", fontSize: 15, fontWeight: 600, borderRadius: 4, border: "1px solid rgba(255,255,255,0.4)", background: "transparent", color: "#fff", cursor: "pointer" },
    barBtnPrimary: { padding: "9px 16px", fontSize: 15, fontWeight: 700, borderRadius: 4, border: "none", background: VL.yellow, color: VL.ink, cursor: "pointer" },
    showBar: { position: "fixed", top: 12, left: 12, zIndex: 30, width: 42, height: 42, borderRadius: 4, border: "none", background: VL.ink, color: "#fff", fontSize: 18, cursor: "pointer" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(430px, 1fr))", gap: 16, padding: 18, alignItems: "start" },
    empty: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "80vh", textAlign: "center" },

    panel: { background: "#fff", border: `1px solid ${VL.gray200}`, borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
    panelHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, padding: "12px 16px", background: VL.blue, color: "#fff" },
    panelTitle: { fontSize: 21, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    panelSub: { fontSize: 13, color: "rgba(255,255,255,0.85)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 },
    panelClose: { background: "transparent", border: "none", color: "rgba(255,255,255,0.85)", fontSize: 16, cursor: "pointer", flexShrink: 0 },
    tableWrap: { flex: 1 },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 18 },
    rowAlt: { background: VL.gray25 },
    rowLeader: { background: VL.yellowTint },
    tdRank: { padding: "11px 8px 11px 16px", width: 46, textAlign: "right", fontWeight: 800, color: VL.blue, fontVariantNumeric: "tabular-nums" },
    tdRankLeader: { padding: "11px 8px 11px 16px", width: 46, textAlign: "right", fontWeight: 800, color: VL.yellowDark, fontVariantNumeric: "tabular-nums" },
    tdBib: { padding: "11px 8px", width: 60, color: VL.gray600, fontVariantNumeric: "tabular-nums" },
    tdName: { padding: "11px 8px" },
    nameMain: { fontWeight: 700, color: VL.ink },
    nameSub: { fontSize: 12, color: VL.gray600, marginTop: 1 },
    tdTime: { padding: "11px 16px 11px 8px", textAlign: "right", fontWeight: 800, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: VL.ink },
    panelMsg: { padding: 24, textAlign: "center", color: VL.gray600 },
    panelFoot: { padding: "7px 16px", fontSize: 11, color: VL.gray400, borderTop: `1px solid ${VL.gray50}` },

    overlay: { position: "fixed", inset: 0, background: "rgba(34,34,35,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, zIndex: 40, overflowY: "auto" },
    wizard: { background: "#fff", border: `1px solid ${VL.gray200}`, borderRadius: 12, padding: 22, width: "100%", maxWidth: 460 },
    wizardHead: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 20, fontWeight: 800, marginBottom: 16 },
    field: { display: "block", marginBottom: 12 },
    label: { display: "block", fontSize: 13, fontWeight: 600, color: VL.gray600, marginBottom: 5 },
    linkAction: { background: "transparent", border: "none", color: VL.blue, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0 },
    splitBox: { border: `1px solid ${VL.gray200}`, borderRadius: 6, maxHeight: 190, overflowY: "auto", padding: 4 },
    splitRow: { display: "flex", alignItems: "center", gap: 10, padding: "9px 8px", fontSize: 15, cursor: "pointer", borderRadius: 4 },
    select: { width: "100%", padding: "12px", fontSize: 16, borderRadius: 6, border: `1px solid ${VL.gray200}`, background: "#fff", color: VL.ink, boxSizing: "border-box" },
    primaryBtn: { width: "100%", padding: "14px", fontSize: 17, fontWeight: 800, borderRadius: 6, border: "none", background: VL.blue, color: "#fff", cursor: "pointer", marginTop: 6 },
    linkBtn: { background: "transparent", border: "none", color: VL.gray600, fontSize: 15, cursor: "pointer" },
};

export default ResultsDisplay;
